/**
 * DynamicLoaderComponent is a wrapper around LoaderGhostGroup that accepts responsive layout and height configurations.
 *
 * @module views/components/DynamicLoaderComponent
 * @memberof -Common
 */
import './DynamicLoaderComponent.scss';

import React from 'react';

import PropTypes from 'prop-types';
import { LoaderGhostGroup } from 'web-palette/dist/components/LoaderGhostGroup/LoaderGhostGroup';

import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';

/**
 * Represents a DynamicLoaderComponent
 *
 * @method
 * @param {DynamicLoaderComponentProps} props - React properties passed from composition
 * @returns DynamicLoaderComponent
 */
export const DynamicLoaderComponent = ( props ) => {
  const {
    minHeights,
    numberAcross = defaultProps.numberAcross,
    numberRows = defaultProps.numberRows,
    showTitle = defaultProps.showTitle,
    titleAlignment = defaultProps.titleAlignment,
    variant = defaultProps.variant
  } = props;
  const { breakpoint: { CURRENT_BREAKPOINT } } = useDeviceInflection();

  return (
    <div className='DynamicLoaderComponent'>
      <LoaderGhostGroup
        showTitle={ showTitle }
        titleAlignment={ titleAlignment }
        numberAcrossSm={ numberAcross.sm }
        numberAcrossMd={ numberAcross.md }
        numberAcrossLg={ numberAcross.lg }
        numberAcrossXl={ numberAcross.xl }
        numberRowsSm={ numberRows.sm }
        numberRowsMd={ numberRows.md }
        numberRowsLg={ numberRows.lg }
        numberRowsXl={ numberRows.xl }
        variant={ variant }
        cardHeightSm={ minHeights?.sm }
        cardHeightMd={ minHeights?.md }
        cardHeightLg={ minHeights?.lg }
        cardHeightXl={ minHeights?.xl }
        breakpoint={ CURRENT_BREAKPOINT }
      />
    </div>
  );
};

/**
 * Property type definitions
 * @typedef DynamicLoaderComponentProps
 * @type {object}
 * @property {object} minHeights - Responsive minimum heights for each breakpoint { sm, md, lg, xl }. Values should be CSS length strings (e.g., '10rem').
 * @property {object} [numberAcross={ sm: 1, md: 1, lg: 1, xl: 1 }] - Responsive number of cards across for each breakpoint.
 * @property {object} [numberRows={ sm: 1, md: 1, lg: 1, xl: 1 }] - Responsive number of rows for each breakpoint.
 * @property {boolean} [showTitle=false] - Controls whether a title skeleton row is shown above the cards.
 * @property {string} [titleAlignment='left'] - Controls horizontal alignment of the title skeleton.
 * @property {string} [variant='default'] - Controls the visual layout of each LoaderGhostCard.
 */
export const propTypes = {
  /**
   * Responsive minimum heights for each breakpoint.
   * Expects an object with keys: sm, md, lg, xl.
   * Each value should be a CSS length string (for example: '32.2rem').
   */
  minHeights: PropTypes.shape( {
    sm: PropTypes.string,
    md: PropTypes.string,
    lg: PropTypes.string,
    xl: PropTypes.string
  } ).isRequired,
  /**
   * Responsive number of cards across for each breakpoint.
   * Expects an object with keys: sm, md, lg, xl.
   */
  numberAcross: PropTypes.shape( {
    sm: PropTypes.number,
    md: PropTypes.number,
    lg: PropTypes.number,
    xl: PropTypes.number
  } ),
  /**
   * Responsive number of rows for each breakpoint.
   * Expects an object with keys: sm, md, lg, xl.
   */
  numberRows: PropTypes.shape( {
    sm: PropTypes.number,
    md: PropTypes.number,
    lg: PropTypes.number,
    xl: PropTypes.number
  } ),
  /**
   * Controls whether a title skeleton row is shown above the cards.
   */
  showTitle: PropTypes.bool,
  /**
   * Controls horizontal alignment of the title skeleton.
   */
  titleAlignment: PropTypes.oneOf( ['left', 'center'] ),
  /**
   * Controls the visual layout of each LoaderGhostCard.
   */
  variant: PropTypes.string
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {object} numberAcross={ sm: 1, md: 1, lg: 1, xl: 1 } - Responsive number of cards across for each breakpoint.
 * @property {object} numberRows={ sm: 1, md: 1, lg: 1, xl: 1 } - Responsive number of rows for each breakpoint.
 * @property {boolean} showTitle=false - Shows the title skeleton row by default.
 * @property {string} titleAlignment='left' - Left aligns the title skeleton by default.
 * @property {string} variant='default' - Uses the default LoaderGhostCard variant by default.
 */
export const defaultProps = {
  numberAcross: {
    sm: 1,
    md: 1,
    lg: 1,
    xl: 1
  },
  numberRows: {
    sm: 1,
    md: 1,
    lg: 1,
    xl: 1
  },
  showTitle: false,
  titleAlignment: 'left',
  variant: 'default'
};

DynamicLoaderComponent.propTypes = propTypes;
DynamicLoaderComponent.defaultProps = defaultProps;

export default DynamicLoaderComponent;
